import { type MultiSelectControl, useFilterUnits } from 'noddi-ui-web';
import { fleetViewSummaryFilterSpecs } from './fleetViewSummaryFilterSpecs';
import { defaultFleetViewFilterTranslations } from './fleetViewTranslations';

/**
 * The fleet view filters the summary charts select by clicking a slice, on the same URL state as the table's
 * controls. Their counts take the list's own params (`FleetViewSummaryCharts`' `filterParams`), not these.
 */
export const useFleetViewSummaryFilters = () => {
  // Text is not URL state: the charts' specs read and write the same params as the table's, whatever its language.
  const specs = fleetViewSummaryFilterSpecs(defaultFleetViewFilterTranslations);
  const filters = useFilterUnits([
    specs.inStorageSeason,
    specs.tireWearStatusSummer,
    specs.tireWearStatusWinter,
    specs.hasBookingItemUpcoming,
    specs.upcomingBookingDate,
    specs.lastBookingDate
  ]);

  const inStorageSeason = selection(filters.controlOf(specs.inStorageSeason));
  const tireWearStatusSummer = selection(filters.controlOf(specs.tireWearStatusSummer));
  const tireWearStatusWinter = selection(filters.controlOf(specs.tireWearStatusWinter));
  const hasBookingItemUpcoming = filters.controlOf(specs.hasBookingItemUpcoming);

  return {
    inStorageSeason: inStorageSeason.values,
    setInStorageSeason: inStorageSeason.setValues,
    tireWearStatusSummer: tireWearStatusSummer.values,
    setTireWearStatusSummer: tireWearStatusSummer.setValues,
    tireWearStatusWinter: tireWearStatusWinter.values,
    setTireWearStatusWinter: tireWearStatusWinter.setValues,
    hasBookingItemUpcoming: hasBookingItemUpcoming?.value ?? null,
    setHasBookingItemUpcoming: (value: boolean | null) => hasBookingItemUpcoming?.onChange(value)
  };
};

/** A slice click selects exactly those values, so none of them stays excluded. */
const selection = (control: MultiSelectControl | undefined) => ({
  values: control?.value.include.map(String) ?? [],
  setValues: (values: string[]) =>
    control?.onChange({
      include: values,
      exclude: control.value.exclude.filter((value) => !values.includes(String(value)))
    })
});
